import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Invoice, CreateInvoiceDTO, UpdateInvoiceDTO } from '@/types/models/Invoice';
import { InvoiceService } from '@/services';
import { logger } from '@/lib/logger';
import { RootState } from '@/state/store';

interface InvoicesState {
  invoices: Invoice[];
  currentInvoice: Invoice | null;
  isLoading: boolean;
  currentStoreId: string | null;
  isRecentOrdersLoading: boolean;
  isRecentInvoicesLoading: boolean;
  recentOrders: Invoice[];
  recentCompletedInvoices: Invoice[];
  error: string | null;
  pagination: {
    page: number;
    hasMore: boolean;
    total: number;
    isLoadingMore: boolean;
  };
  recentOrdersStoreId: string | null;
  recentCompletedInvoicesStoreId: string | null;
  stats: {
    totalInvoices: number;
    newToday: number;
  };
  isStatsLoading: boolean;
  // Invoice cache system
  invoiceCache: {
    [invoiceId: string]: {
      invoice: Invoice;
      lastModified: string;
      cloudLastModified: string;
    };
  };
  cachedInvoiceIds: string[];
}

const initialState: InvoicesState = {
  invoices: [],
  currentInvoice: null,
  isLoading: false,
  currentStoreId: null,
  isRecentOrdersLoading: false,
  isRecentInvoicesLoading: false,
  recentOrders: [],
  recentCompletedInvoices: [],
  error: null,
  pagination: {
    page: 1,
    hasMore: true,
    total: 0,
    isLoadingMore: false,
  },
  recentOrdersStoreId: null,
  recentCompletedInvoicesStoreId: null,
  stats: {
    totalInvoices: 0,
    newToday: 0,
  },
  isStatsLoading: false,
  // Invoice cache system
  invoiceCache: {},
  cachedInvoiceIds: [],
};

export const fetchOrderedInvoices = createAsyncThunk(
  'invoices/fetchAllOrdered',
  async (storeId: string) => {
    logger.info('Fetching invoices', { storeId });
    return InvoiceService.getAllOrdered(storeId);
  }
);

export const fetchCompletedInvoices = createAsyncThunk(
  'invoices/fetchAllCompleted',
  async (storeId: string) => {
    logger.info('Fetching completed invoices', { storeId });
    return InvoiceService.getAllCompleted(storeId);
  }
);

export const fetchInvoiceById = createAsyncThunk(
  'invoices/fetchById',
  async (id: string) => {
    logger.info('Fetching invoice by ID', { id });
    return InvoiceService.getById(id);
  }
);

export const createInvoice = createAsyncThunk(
  'invoices/create',
  async (data: CreateInvoiceDTO) => {
    logger.info('Creating invoice', { data });
    return InvoiceService.create(data);
  }
);

export const updateInvoice = createAsyncThunk(
  'invoices/update',
  async ({ id, data }: { id: string; data: UpdateInvoiceDTO }) => {
    logger.info('Updating invoice', { id, data });
    return InvoiceService.update(id, data);
  }
);

export const deleteInvoice = createAsyncThunk(
  'invoices/delete',
  async (id: string) => {
    logger.info('Deleting invoice', { id });
    await InvoiceService.delete(id);
    return id;
  }
);

export const fetchRecentOrders = createAsyncThunk(
  'invoices/fetchRecentOrders',
  async (storeId: string) => {
    try {
      logger.info('Fetching recent orders', { storeId });
      return await InvoiceService.getRecentOrders(storeId);
    } catch (error) {
      logger.error('Failed to fetch recent orders', { error, storeId });
      throw error;
    }
  }
);

export const fetchRecentCompletedInvoices = createAsyncThunk(
  'invoices/fetchRecentCompletedInvoices',
  async (storeId: string) => {
    try {
      logger.info('Fetching recent completed invoices', { storeId });
      return await InvoiceService.getRecentCompletedInvoices(storeId);
    } catch (error) {
      logger.error('Failed to fetch recent completed invoices', { error, storeId });
      throw error;
    }
  }
);

export const fetchAllCompletedInvoicesAdmin = createAsyncThunk(
  'invoices/fetchAllCompletedInvoicesAdmin',
  async () => {
    return InvoiceService.getAllCompletedAdmin();
  }
);

export const fetchCompletedInvoicesPaginated = createAsyncThunk(
  'invoices/fetchCompletedPaginated',
  async ({ storeId, page, searchQuery, status, paymentStatus, dateFrom, dateTo }: {
    storeId: string;
    page: number;
    searchQuery?: string;
    status?: string;
    paymentStatus?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    logger.info('Fetching invoices with pagination and filters', { storeId, page, searchQuery, status, paymentStatus, dateFrom, dateTo });
    return InvoiceService.getInvoicesPaginated(storeId, page, {
      searchQuery,
      status,
      paymentStatus,
      dateFrom,
      dateTo,
    });
  }
);

export const fetchInvoiceStats = createAsyncThunk(
  'invoices/fetchStats',
  async (storeId: string) => {
    logger.info('Fetching invoice stats', { storeId });
    return InvoiceService.getInvoiceStats(storeId);
  }
);

// Invoice cache actions
export const cacheInvoice = createAsyncThunk(
  'invoices/cacheInvoice',
  async ({ invoiceId, invoice, cloudLastModified }: { 
    invoiceId: string; 
    invoice: Invoice; 
    cloudLastModified: string; 
  }) => {
    const lastModified = new Date().toISOString();
    
    logger.debug('Caching invoice to local storage', {
      component: 'InvoicesSlice',
      data: {
        invoiceId,
        customerName: invoice.customer_name,
        itemCount: invoice.item_count,
        billAmount: invoice.bill_amount,
        lastModified,
        cloudLastModified,
        lineItemsCount: invoice.line_items?.length || 0
      }
    });
    
    return { 
      invoiceId, 
      invoice, 
      cloudLastModified, 
      lastModified
    };
  }
);

export const clearCachedInvoice = createAsyncThunk(
  'invoices/clearCachedInvoice',
  async (invoiceId: string) => {
    logger.debug('Clearing cached invoice from local storage', {
      component: 'InvoicesSlice',
      data: { invoiceId }
    });
    return invoiceId;
  }
);

// Note: load actions are no longer needed
// Redux Persist automatically handles loading from localStorage

const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null;
    },
    clearInvoices: (state) => {
      state.invoices = [];
      state.recentOrders = [];
      state.recentCompletedInvoices = [];
      state.currentStoreId = null;
      state.error = null;
      state.pagination = {
        page: 1,
        hasMore: true,
        total: 0,
        isLoadingMore: false,
      };
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        hasMore: true,
        total: 0,
        isLoadingMore: false,
      };
    },
    setSearchQuery: (state) => {
      state.pagination = {
        page: 1,
        hasMore: true,
        total: 0,
        isLoadingMore: false,
      };
      state.invoices = [];
    },
    clearAllCachedInvoices: (state) => {
      state.invoiceCache = {};
      state.cachedInvoiceIds = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch All Invoices
    builder
      .addCase(fetchOrderedInvoices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderedInvoices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchOrderedInvoices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch invoices';
        logger.error('Failed to fetch invoices', { error: action.error });
      });

    // Fetch Completed Invoices
    builder
      .addCase(fetchCompletedInvoices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompletedInvoices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchCompletedInvoices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch completed invoices';
        logger.error('Failed to fetch completed invoices', { error: action.error });
      });

    // Handle paginated fetch
    builder
      .addCase(fetchCompletedInvoicesPaginated.pending, (state, action) => {
        const { page } = action.meta.arg;
        if (page === 1) {
          state.isLoading = true;
        } else {
          state.pagination.isLoadingMore = true;
        }
        state.error = null;
      })
      .addCase(fetchCompletedInvoicesPaginated.fulfilled, (state, action) => {
        const { page } = action.meta.arg;
        const { data, hasMore, total } = action.payload;
        
        if (page === 1) {
          state.invoices = data;
          state.isLoading = false;
        } else {
          state.invoices = [...state.invoices, ...data];
          state.pagination.isLoadingMore = false;
        }
        
        state.pagination = {
          ...state.pagination,
          page,
          hasMore,
          total,
          isLoadingMore: false,
        };
      })
      .addCase(fetchCompletedInvoicesPaginated.rejected, (state, action) => {
        const { page } = action.meta.arg;
        if (page === 1) {
          state.isLoading = false;
        } else {
          state.pagination.isLoadingMore = false;
        }
        state.error = action.error.message || 'Failed to fetch invoices';
        logger.error('Failed to fetch invoices with pagination', { error: action.error });
      });

    // Fetch All Completed Invoices (Admin)
    builder
      .addCase(fetchAllCompletedInvoicesAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCompletedInvoicesAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchAllCompletedInvoicesAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch all completed invoices (admin)';
        logger.error('Failed to fetch all completed invoices (admin)', { error: action.error });
      });

    // Fetch Invoice by ID
    builder
      .addCase(fetchInvoiceById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentInvoice = action.payload;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch invoice';
        logger.error('Failed to fetch invoice', { error: action.error });
      });

    // Create Invoice
    builder
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.invoices.unshift(action.payload);
        state.currentInvoice = action.payload;
      })
      .addCase(createInvoice.rejected, (action) => {
        logger.error('Failed to create invoice', { error: action.error });
      });

    // Update Invoice
    builder
      .addCase(updateInvoice.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(invoice => invoice.id === action.payload.id);
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
        state.currentInvoice = action.payload;
      })
      .addCase(updateInvoice.rejected, (action) => {
        logger.error('Failed to update invoice', { error: action.error });
      });

    // Delete Invoice
    builder
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.invoices = state.invoices.filter(invoice => invoice.id !== action.payload);
        if (state.currentInvoice?.id === action.payload) {
          state.currentInvoice = null;
        }
      })
      .addCase(deleteInvoice.rejected, (action) => {
        logger.error('Failed to delete invoice', { error: action.error });
      });

    // Recent Orders
    builder
      .addCase(fetchRecentOrders.pending, (state) => {
        state.isRecentOrdersLoading = true;
        state.error = null;
      })
      .addCase(fetchRecentOrders.fulfilled, (state, action) => {
        state.isRecentOrdersLoading = false;
        state.recentOrders = action.payload;
        state.recentOrdersStoreId = action.meta.arg;
      })
      .addCase(fetchRecentOrders.rejected, (state, action) => {
        state.isRecentOrdersLoading = false;
        state.error = action.error.message || 'Failed to fetch recent orders';
      });

    // Recent Completed Invoices
    builder
      .addCase(fetchRecentCompletedInvoices.pending, (state) => {
        state.isRecentInvoicesLoading = true;
        state.error = null;
      })
      .addCase(fetchRecentCompletedInvoices.fulfilled, (state, action) => {
        state.isRecentInvoicesLoading = false;
        state.recentCompletedInvoices = action.payload;
        state.recentCompletedInvoicesStoreId = action.meta.arg;
      })
      .addCase(fetchRecentCompletedInvoices.rejected, (state, action) => {
        state.isRecentInvoicesLoading = false;
        state.error = action.error.message || 'Failed to fetch recent completed invoices';
      });

    // Fetch Invoice Stats
    builder
      .addCase(fetchInvoiceStats.pending, (state) => {
        state.isStatsLoading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceStats.fulfilled, (state, action) => {
        state.isStatsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchInvoiceStats.rejected, (state, action) => {
        state.isStatsLoading = false;
        state.error = action.error.message || 'Failed to fetch invoice stats';
        logger.error('Failed to fetch invoice stats', { error: action.error });
      });

    // Invoice cache reducers
    builder
      .addCase(cacheInvoice.fulfilled, (state, action) => {
        const { invoiceId, invoice, cloudLastModified, lastModified } = action.payload;
        state.invoiceCache[invoiceId] = {
          invoice,
          cloudLastModified,
          lastModified
        };
        if (!state.cachedInvoiceIds.includes(invoiceId)) {
          state.cachedInvoiceIds.push(invoiceId);
        }
        // Redux Persist will handle persistence automatically
      })
      .addCase(clearCachedInvoice.fulfilled, (state, action) => {
        const invoiceId = action.payload;
        delete state.invoiceCache[invoiceId];
        state.cachedInvoiceIds = state.cachedInvoiceIds.filter(id => id !== invoiceId);
        // Redux Persist will handle persistence automatically
      });
  },
});

export const { clearCurrentInvoice, clearInvoices, resetPagination, setSearchQuery, clearAllCachedInvoices } = invoicesSlice.actions;
export default invoicesSlice.reducer;

// Selectors
export const selectRecentOrders = (state: { invoices: InvoicesState }) => state.invoices.recentOrders;
export const selectRecentCompletedInvoices = (state: { invoices: InvoicesState }) => state.invoices.recentCompletedInvoices;
export const selectIsRecentOrdersLoading = (state: { invoices: InvoicesState }) => state.invoices.isRecentOrdersLoading;
export const selectIsRecentInvoicesLoading = (state: { invoices: InvoicesState }) => state.invoices.isRecentInvoicesLoading;
export const selectCurrentStoreId = (state: { invoices: InvoicesState }) => state.invoices.currentStoreId;
export const selectRecentOrdersStoreId = (state: { invoices: InvoicesState }) => state.invoices.recentOrdersStoreId;
export const selectRecentCompletedInvoicesStoreId = (state: { invoices: InvoicesState }) => state.invoices.recentCompletedInvoicesStoreId;
export const selectCurrentInvoice : (state: RootState) => Invoice | null = (state: RootState) => state.invoices.currentInvoice as Invoice | null;